import { useState, useEffect, useCallback } from 'react';
import DeviceData from 'drivers/deviceData';
import getIsRTLLanguage from 'utils/common/isRTL';

/**
 * Custom hook for managing portal navigation and keyboard controls
 * @param {Array} items - Array of navigable items
 * @param {Function} onCategorySelect - Callback when a category is selected
 * @param {Function} onBackPress - Callback when back key is pressed
 * @returns {Object} Navigation state and methods
 */
export const usePortalNavigation = (items = [], onCategorySelect, onBackPress) => {
  const [focusIndex, setFocusIndex] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);

  const navigateToIndex = useCallback(
    (newIndex) => {
      if (!items.length) return;

      let validIndex = newIndex;
      
      // Handle circular navigation
      if (validIndex >= items.length) validIndex = 0;
      if (validIndex < 0) validIndex = items.length - 1;

      if (validIndex !== focusIndex) {
        setFocusIndex(validIndex);
        
        // Call the callback with the selected item
        if (onCategorySelect && items[validIndex]) {
          onCategorySelect(validIndex, items[validIndex]);
        }
      }
    },
    [focusIndex, items, onCategorySelect]
  );

  const handleKeyPress = useCallback(
    async (event) => {
      const tvKeyControls = await DeviceData.getTVControl();
      const isRTL = getIsRTLLanguage();
      
      // Adjust navigation direction for RTL languages
      const KEY_LEFT_DIRECTION = isRTL ? tvKeyControls.KEY_RIGHT : tvKeyControls.KEY_LEFT;
      const KEY_RIGHT_DIRECTION = isRTL ? tvKeyControls.KEY_LEFT : tvKeyControls.KEY_RIGHT;

      let newIndex;

      switch (event.keyCode) {
        case KEY_RIGHT_DIRECTION:
          newIndex = focusIndex + 1;
          navigateToIndex(newIndex);
          break;
          
        case KEY_LEFT_DIRECTION:
          newIndex = focusIndex - 1;
          navigateToIndex(newIndex);
          break;
          
        case tvKeyControls.KEY_BACK:
          if (onBackPress) {
            onBackPress(event);
          }
          event.preventDefault();
          break;
          
        case tvKeyControls.KEY_ENTER:
        case tvKeyControls.KEY_OK:
          // Allow manual selection with Enter/OK
          if (onCategorySelect && items[focusIndex]) {
            onCategorySelect(focusIndex, items[focusIndex]);
          }
          break;
          
        default:
          break;
      }
    },
    [focusIndex, items, navigateToIndex, onBackPress, onCategorySelect]
  );

  // Reset focus when items change
  useEffect(() => {
    if (items.length > 0 && focusIndex >= items.length) {
      setFocusIndex(0);
    }
  }, [items.length, focusIndex]);

  // Set up keyboard event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  const manuallySetFocus = useCallback((index) => {
    if (index >= 0 && index < items.length) {
      setFocusIndex(index);
    }
  }, [items.length]);

  return {
    focusIndex,
    scrollPosition,
    setScrollPosition,
    navigateToIndex,
    manuallySetFocus,
  };
};
